import React, { forwardRef, useRef } from "react";
import { DatePicker, Input, isNullish, Portal, ToolIcon } from "@theorgchartdev/storybook";
import { isValidDate } from "@coreapp/utils/isValidDate";
import { isBlockedField, getBlockedFieldDisplayValue } from "@coreapp/utils/FormatFieldValue";

export const dateFormats = {
  0: "MMMM dd, yyyy", // "January 28, 2016"
  1: "dd MMMM yyyy", // "28 January 2016"
  2: "dd MMM yyyy", // "28 Jan 2016"
  3: "M/dd/yyyy", // "1/28/2016"
  4: "M-dd-yyyy", // "1-28-2016"
  5: "M.dd.yyyy", // "1.28.2016"
  6: "M/dd/yy", // "1/28/16"
  7: "MM-dd-yy", // "01-28-16"
  8: "MM.dd.yy", // "01.28.16"
  9: "dd/MM/yyyy", // "28/01/2016"
  10: "dd-MM-yyyy", // "28-01-2016"
  11: "dd.MM.yyyy", // "28.01.2016"
  12: "dd/MM/yy", // "28/01/16"
  13: "dd-MM-yy", // "28-01-16"
  14: "dd.MM.yy", // "28.01.16"
  15: "MM-yyyy", // "01-2016"
  16: "MMMM yyyy", // "January 2016"
  17: "MMMM-yyyy", // "January-2016"
  18: "dd (day)", // "28 (day)"
  19: "M (month)", // "1 (month)"
  20: "MMMM", // "January"
  21: "yyyy (year)", // "2016 (year)"
  22: "yyyy/MM/dd", // "2019/06/19"
  23: "yyyyMMdd", // "20190619"
  24: "yyyy-MM-dd" // "2019-06-19"
};

export const CustomDateInput = forwardRef(({ onValueChange, ...props }, ref) => {
  const handleChange = (event, value) => {
    if (!props.inputRef.current.state.open) props.inputRef.current.setOpen(event);
    if (onValueChange && value === "") {
      if (props.inputRef) props.inputRef.current.clear();
      onValueChange(value, event);
    } else {
      props?.onChange(event);
    }
  };

  const handleCalendarIconClick = event => {
    if (!props.inputRef) return;
    props.inputRef.current.setOpen(event);
    props.inputRef.current.sendFocusBackToInput();
  };

  return (
    <Input
      {...props}
      ref={ref}
      postfixNode={
        <ToolIcon
          name="calendar"
          onClick={handleCalendarIconClick}
          size="xsmall"
          color="grey"
          className="date-input-icon"
        />
      }
      onChange={handleChange}
    />
  );
});

const CalendarPortal = ({ children }) => <Portal open>{children}</Portal>;

const DateComponentField = ({
  value,
  fieldDef,
  onChange,
  enumDateFormat,
  customInput,
  returnDateObject,
  ...props
}) => {
  const inputRef = useRef(null);
  const handleDateChange = (nextDate, event) => {
    const validDate = isValidDate(nextDate);
    // Optionally pass raw date object to change handler
    if (returnDateObject) {
      const nextDateValue = validDate ? nextDate : "";
      onChange(event, nextDateValue);
      return;
    }
    // Force iso format dd/mm/yyyy on valid date selections
    const dateStr = validDate ? nextDate.FormatDate(3) : "";
    onChange(event, dateStr);
  };

  const validDate = stringDate => {
    return !isNullish(stringDate) && isValidDate(new Date(stringDate));
  };

  const getSelectedDate = () => {
    let selectedDate = null;
    let dateStr = "";
    if (validDate(value)) {
      selectedDate = new Date(value);
      dateStr = selectedDate.FormatDate(3);
    } else if (isBlockedField(value) || value === getBlockedFieldDisplayValue()) {
      dateStr = getBlockedFieldDisplayValue();
    }
    return { selectedDate, dateStr };
  };

  const { selectedDate, dateStr } = getSelectedDate();
  const formatEnum = !isNullish(enumDateFormat) ? enumDateFormat : fieldDef.GetDateFormat();
  return (
    <DatePicker
      ref={inputRef}
      popperContainer={CalendarPortal}
      popperClassName={"oc-date-picker"}
      className={"custom-datepicker"}
      customInput={
        <CustomDateInput
          onValueChange={handleDateChange}
          value={dateStr}
          {...props}
          inputRef={inputRef}
        />
      }
      onChange={handleDateChange}
      selected={selectedDate}
      dateFormat={dateFormats[formatEnum] || "MMMM dd, yyyy"}
      {...props}
      placeholderText={props?.placeholder}
    />
  );
};

export default DateComponentField;
